import { useEffect, useRef } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans } from "@lingui/react/macro";
import { useConvexAuth, useQuery } from "convex/react";
import {
  Check,
  ClipboardCheck,
  Clock,
  Euro,
  Layers,
  Leaf,
  Share2,
  Smartphone,
  X,
} from "lucide-react";

import { api } from "../../convex/_generated/api";
import type { TemplateId } from "../../convex/templateTypes";
import { clearBuilderFlowState } from "../lib/builderFlowStorage";
import TemplateGalleryGrid from "../components/builder/TemplateGalleryGrid";
import {
  DesktopMockup,
  MobilePreviewCarousel,
  PhoneMockup,
} from "../components/DeviceMockup";
import LandingLayout from "../components/LandingLayout";

export const Route = createFileRoute("/")({ component: IndexPage });

function IndexPage() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const navigate = useNavigate();
  const galleryRef = useRef<HTMLElement | null>(null);
  const mySites = useQuery(
    api.weddingSites.getMySites,
    isAuthenticated ? {} : "skip",
  );

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const startOnboarding = () => {
    clearBuilderFlowState();

    if (isAuthenticated) {
      void navigate({
        search: { step: undefined, template: undefined },
        to: "/create",
      });
      return;
    }

    void navigate({
      search: { entry: "cta", flow: "signup", template: undefined },
      to: "/auth/signin",
    });
  };

  const openTemplatePreview = (templateId: TemplateId) => {
    void navigate({
      params: { templateId },
      to: "/templates/$templateId",
    });
  };

  useEffect(() => {
    if (!isAuthenticated || mySites === undefined) {
      return;
    }
    if (mySites.length > 0) {
      navigate({ params: { slug: mySites[0].slug }, to: "/admin/$slug" });
      return;
    }
    clearBuilderFlowState();
    navigate({
      search: { step: undefined, template: undefined },
      to: "/create",
    });
  }, [isAuthenticated, mySites, navigate]);

  if (authLoading || (isAuthenticated && mySites === undefined)) {
    return (
      <LandingLayout>
        <main className="flex min-h-screen items-center justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
        </main>
      </LandingLayout>
    );
  }

  if (isAuthenticated && mySites) {
    return (
      <LandingLayout>
        <main className="flex min-h-screen items-center justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
        </main>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout showFooter>
      <main className="overflow-x-hidden">
        <section className="bg-white pb-16 pt-20 sm:pt-24 lg:pb-24 lg:pt-28">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h1 className="mx-auto max-w-4xl text-center font-display text-5xl italic leading-[0.95] tracking-[-0.02em] text-[var(--color-ink)] sm:text-6xl lg:text-7xl">
              <Trans>Sitios web e invitaciones de boda modernas</Trans>
            </h1>
            <div className="mt-8 flex justify-center gap-4">
              <button
                className="app-btn-primary"
                data-testid="landing-get-started"
                onClick={startOnboarding}
                type="button"
              >
                <Trans>Empezar Gratis</Trans>
              </button>
              <button
                className="app-btn-secondary"
                data-testid="landing-view-templates"
                onClick={scrollToGallery}
                type="button"
              >
                <Trans>Ver plantillas</Trans>
              </button>
            </div>
            <div className="mt-14 sm:hidden">
              <MobilePreviewCarousel />
            </div>
            <div className="mt-14 hidden items-end justify-center gap-4 sm:flex lg:gap-8">
              <PhoneMockup />
              <DesktopMockup />
            </div>
          </div>
        </section>

        <section
          className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-24"
          data-testid="landing-template-gallery"
          id="gallery"
          ref={galleryRef}
        >
          <div className="mb-10 max-w-2xl">
            <h2 className="font-display text-5xl italic leading-none text-[var(--color-ink)]">
              <Trans>Explora la colección completa.</Trans>
            </h2>
          </div>

          <TemplateGalleryGrid
            cardTestIdPrefix="landing-template-card"
            onTemplateClick={openTemplatePreview}
          />
        </section>

        <HowItWorksSection
          onStart={startOnboarding}
          onViewGallery={scrollToGallery}
        />
        <ComparisonSection />
        <TestimonialsSection />
        <FaqSection />
      </main>
    </LandingLayout>
  );
}

function HowItWorksSection({
  onStart,
  onViewGallery,
}: {
  onStart: () => void;
  onViewGallery: () => void;
}) {
  const steps = [
    {
      description: (
        <Trans>
          Explora la{" "}
          <button
            className="app-link cursor-pointer"
            data-testid="landing-how-step-gallery-link"
            onClick={onViewGallery}
            type="button"
          >
            colección
          </button>{" "}
          y elige el diseño que mejor encaja con el estilo de tu boda.
        </Trans>
      ),
      number: "01",
      title: <Trans>Elige una plantilla</Trans>,
    },
    {
      description: (
        <Trans>
          Añade la fecha, el lugar, tu historia y las fotos que quieres
          compartir con los invitados.
        </Trans>
      ),
      number: "02",
      title: <Trans>Completa los detalles</Trans>,
    },
    {
      description: (
        <Trans>
          Regístrate para publicar el sitio, gestionar las confirmaciones y
          editarlo cuando quieras.
        </Trans>
      ),
      number: "03",
      title: <Trans>Crea tu cuenta</Trans>,
    },
  ];

  return (
    <section
      className="border-t border-[var(--color-border)] bg-[var(--color-page-soft)] py-20 lg:py-24"
      data-testid="landing-how-it-works"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--color-muted)]">
            <Trans>Cómo funciona</Trans>
          </p>
          <h2 className="mt-4 font-display text-5xl italic leading-none text-[var(--color-ink)]">
            <Trans>Tu invitación lista en tres pasos.</Trans>
          </h2>
        </div>

        <ol className="grid gap-8 md:grid-cols-3">
          {steps.map((step) => (
            <li
              className="border-t border-[var(--color-ink)] bg-white p-8"
              data-testid={`landing-how-step-${step.number}`}
              key={step.number}
            >
              <span className="font-display text-5xl italic leading-none text-[var(--color-accent)]">
                {step.number}
              </span>
              <h3 className="mt-6 font-display text-2xl italic leading-tight text-[var(--color-ink)]">
                {step.title}
              </h3>
              <p className="mt-4 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                {step.description}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex justify-center">
          <button
            className="app-btn-primary"
            data-testid="landing-how-it-works-cta"
            onClick={onStart}
            type="button"
          >
            <Trans>Empezar Gratis</Trans>
          </button>
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    {
      digital: <Trans>Gratis, sin impresiones ni envíos.</Trans>,
      feature: <Trans>Coste</Trans>,
      Icon: Euro,
      paper: (
        <Trans>
          Más caro que un mes entero de Premium por solo 5 invitaciones.
        </Trans>
      ),
    },
    {
      digital: <Trans>Editas en un minuto y se actualiza al instante.</Trans>,
      feature: <Trans>Cambios de última hora</Trans>,
      Icon: Clock,
      paper: <Trans>Reimprimir y volver a enviar.</Trans>,
    },
    {
      digital: (
        <Trans>
          Confirmación de asistencia digital integrada con panel de control.
        </Trans>
      ),
      feature: <Trans>Confirmación de asistencia</Trans>,
      Icon: ClipboardCheck,
      paper: <Trans>Llamadas, mensajes y hojas de cálculo.</Trans>,
    },
    {
      digital: <Trans>Fotos, galería, mapa, historia y menú.</Trans>,
      feature: <Trans>Información incluida</Trans>,
      Icon: Layers,
      paper: <Trans>Limitada al espacio del papel.</Trans>,
    },
    {
      digital: <Trans>Un enlace por WhatsApp, email o QR.</Trans>,
      feature: <Trans>Cómo se comparte</Trans>,
      Icon: Share2,
      paper: <Trans>Correo postal o entrega en mano.</Trans>,
    },
    {
      digital: <Trans>Cero papel, cero residuos.</Trans>,
      feature: <Trans>Impacto ambiental</Trans>,
      Icon: Leaf,
      paper: <Trans>Consumo de papel y transporte.</Trans>,
    },
    {
      digital: <Trans>Se ve perfecto en cualquier dispositivo.</Trans>,
      feature: <Trans>Experiencia del invitado</Trans>,
      Icon: Smartphone,
      paper: <Trans>Fija, sin enlaces ni contenido extra.</Trans>,
    },
  ];

  return (
    <section
      className="border-t border-[var(--color-border)] bg-white py-20 lg:py-24"
      data-testid="landing-comparison"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--color-muted)]">
            <Trans>Papel vs. digital</Trans>
          </p>
          <h2 className="mt-4 font-display text-5xl italic leading-none text-[var(--color-ink)]">
            <Trans>Lo que ganas al dejar el papel.</Trans>
          </h2>
        </div>

        <div className="border border-[var(--color-border)]">
          <table className="w-full border-collapse text-left text-sm sm:text-base">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-page-soft)] text-xs uppercase tracking-[0.18em] text-[var(--color-muted)] sm:tracking-[0.22em]">
                <th className="w-[14%] px-2 py-3 font-semibold sm:w-[22%] sm:px-5 sm:py-4">
                  <span className="sr-only">
                    <Trans>Aspecto a comparar</Trans>
                  </span>
                </th>
                <th className="px-2 py-3 font-semibold sm:px-5 sm:py-4">
                  <Trans>En papel</Trans>
                </th>
                <th className="bg-[var(--color-accent-soft)] px-2 py-3 font-semibold text-[var(--color-accent)] sm:px-5 sm:py-4">
                  <Trans>Con BodaWeb</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  className="border-t border-[var(--color-border)] align-top"
                  data-testid={`landing-comparison-row-${index}`}
                  key={index}
                >
                  <td className="px-2 py-4 font-medium text-[var(--color-ink)] sm:px-5 sm:py-5">
                    <span className="flex items-center justify-center sm:hidden">
                      <row.Icon
                        aria-hidden="true"
                        className="h-5 w-5 text-[var(--color-ink)]"
                        strokeWidth={1.75}
                      />
                      <span className="sr-only">{row.feature}</span>
                    </span>
                    <span className="hidden sm:inline">{row.feature}</span>
                  </td>
                  <td className="px-2 py-4 text-[var(--color-muted)] sm:px-5 sm:py-5">
                    <span className="flex gap-2 sm:gap-3">
                      <X
                        aria-hidden="true"
                        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-danger)]"
                        strokeWidth={2}
                      />
                      <span>{row.paper}</span>
                    </span>
                  </td>
                  <td className="bg-[var(--color-accent-soft)]/50 px-2 py-4 text-[var(--color-ink)] sm:px-5 sm:py-5">
                    <span className="flex gap-2 sm:gap-3">
                      <Check
                        aria-hidden="true"
                        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]"
                        strokeWidth={2.2}
                      />
                      <span>{row.digital}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const testimonials = [
    {
      couple: "Laura y Pablo",
      date: <Trans>12 de abril de 2026</Trans>,
      flag: "🇪🇸",
      location: <Trans>Madrid, España</Trans>,
      photo: "/testimonials/couple-1.jpg",
      photoPosition: "right bottom",
      quote: (
        <Trans>
          La armamos en una tarde y fue súper fácil y quedó realmente bonito.
        </Trans>
      ),
    },
    {
      couple: "María y Diego",
      date: <Trans>24 de marzo de 2026</Trans>,
      flag: "🇲🇽",
      location: <Trans>Ciudad de México, México</Trans>,
      photo: "/testimonials/couple-2.jpg",
      photoPosition: "center 70%",
      quote: (
        <Trans>
          Cambiamos el menú dos días antes de la boda y fue solo cuestión de
          actualizar la página.
        </Trans>
      ),
    },
    {
      couple: "Carla y Sergio",
      date: <Trans>8 de marzo de 2026</Trans>,
      flag: "🇨🇴",
      location: <Trans>Bogotá, Colombia</Trans>,
      photo: "/testimonials/couple-3.jpg",
      photoPosition: "center",
      quote: (
        <Trans>Puedes editar casi todo y se ve muy bien en el celular.</Trans>
      ),
    },
  ];

  return (
    <section
      className="border-t border-[var(--color-border)] bg-[var(--color-page-soft)] py-20 lg:py-24"
      data-testid="landing-testimonials"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <h2 className="font-display text-5xl italic leading-none text-[var(--color-ink)]">
            <Trans>Lo que dicen las parejas.</Trans>
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((item, index) => (
            <figure
              className="flex h-full flex-col justify-between border border-[var(--color-border)] bg-white p-8"
              data-testid={`landing-testimonial-${index}`}
              key={item.couple}
            >
              <blockquote className="font-display text-xl italic leading-snug text-[var(--color-ink)] sm:text-2xl">
                <span aria-hidden="true">“</span>
                {item.quote}
                <span aria-hidden="true">”</span>
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-3 border-t border-[var(--color-border)] pt-5">
                <img
                  alt=""
                  className="h-12 w-12 shrink-0 rounded-full object-cover"
                  decoding="async"
                  loading="lazy"
                  src={item.photo}
                  style={{ objectPosition: item.photoPosition }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold uppercase tracking-[0.18em] text-[var(--color-ink)]">
                    {item.couple}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-[var(--color-muted)]">
                    <span aria-hidden="true">{item.flag}</span>
                    <span className="truncate">{item.location}</span>
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    {item.date}
                  </p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    {
      answer: (
        <Trans>
          Sí. Puedes crear tu sitio, publicarlo y compartirlo con tus invitados
          sin pagar nada. Si necesitas funciones avanzadas como recibir más de
          25 confirmaciones o exportar la lista de invitados a CSV, puedes
          pasarte al{" "}
          <Link
            className="app-link cursor-pointer underline"
            data-testid="landing-faq-prices-link"
            to="/prices"
          >
            plan Premium
          </Link>{" "}
          cuando quieras.
        </Trans>
      ),
      question: <Trans>¿Es realmente gratis?</Trans>,
    },
    {
      answer: (
        <Trans>
          No. BodaWeb está pensado para parejas sin experiencia en diseño o
          programación. Eliges una plantilla, completas un formulario guiado y
          publicas con un clic.
        </Trans>
      ),
      question: <Trans>¿Necesito saber de diseño o programación?</Trans>,
    },
    {
      answer: (
        <Trans>
          Solo tardas 30 segundos en crear el sitio. Después puedes dedicar el
          tiempo que quieras a añadir fotos, textos y detalles a tu ritmo.
        </Trans>
      ),
      question: <Trans>¿Cuánto tarda en estar listo?</Trans>,
    },
    {
      answer: (
        <Trans>
          Sí, todo. Puedes cambiar textos, fotos, fechas o el menú en cualquier
          momento y tus invitados verán la versión actualizada al abrir el
          enlace.
        </Trans>
      ),
      question: <Trans>¿Puedo editar la invitación después de publicar?</Trans>,
    },
    {
      answer: (
        <Trans>
          Cada invitación incluye confirmación de asistencia digital. Los
          invitados confirman desde su teléfono y tú ves las respuestas, menús y
          acompañantes en tu panel de administración.
        </Trans>
      ),
      question: <Trans>¿Cómo confirman los invitados su asistencia?</Trans>,
    },
    {
      answer: (
        <Trans>
          En el plan gratuito puedes ver las 25 primeras confirmaciones que
          reciba tu invitación. Las respuestas siguen llegando sin límite, pero
          para ver todas y exportarlas a CSV necesitas suscribirte al plan
          Premium.
        </Trans>
      ),
      question: (
        <Trans>¿Hay un límite de confirmaciones en el plan gratuito?</Trans>
      ),
    },
    {
      answer: (
        <Trans>
          No. Se abre en cualquier navegador moderno, desde el teléfono o la
          computadora. Solo necesitan el enlace que les envíes por WhatsApp,
          email o un código QR.
        </Trans>
      ),
      question: <Trans>¿Mis invitados tienen que instalar algo?</Trans>,
    },
    {
      answer: (
        <Trans>
          Los datos de tus invitados son privados y solo los ves tú desde tu
          cuenta. No compartimos la información con terceros ni enviamos correos
          comerciales a los invitados.
        </Trans>
      ),
      question: <Trans>¿Qué pasa con los datos de mis invitados?</Trans>,
    },
  ];

  return (
    <section
      className="border-t border-[var(--color-border)] bg-white py-20 lg:py-24"
      data-testid="landing-faq"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--color-muted)]">
            <Trans>Preguntas frecuentes</Trans>
          </p>
          <h2 className="mt-4 font-display text-5xl italic leading-none text-[var(--color-ink)]">
            <Trans>Resolvemos tus dudas.</Trans>
          </h2>
        </div>

        <div className="divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
          {faqs.map((faq, index) => (
            <details
              className="group cursor-pointer"
              data-testid={`landing-faq-item-${index}`}
              key={index}
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-left font-display text-xl italic text-[var(--color-ink)] transition-colors hover:text-[var(--color-accent)] sm:text-2xl">
                <span>{faq.question}</span>
                <span
                  aria-hidden="true"
                  className="text-2xl font-light text-[var(--color-muted)] transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <div className="pb-6 pr-10 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-7 text-[var(--color-muted)] sm:text-base">
            <Trans>¿Quieres ver el detalle completo de los planes?</Trans>
          </p>
          <Link
            className="app-link cursor-pointer text-sm font-semibold sm:text-base"
            data-testid="landing-faq-prices-cta"
            to="/prices"
          >
            <Trans>Ver precios →</Trans>
          </Link>
        </div>
      </div>
    </section>
  );
}
